import React, { useCallback, useEffect, useState } from 'react';
import axios from 'axios';
import { Link, useLocation, useParams } from 'react-router-dom';
import { FaCheckCircle, FaExclamationTriangle, FaTimesCircle, FaCamera } from 'react-icons/fa';
import SEO from './SEO';
import SimpleSpinner from './SimpleSpinner';
import QRScanner from './QRScanner';
import '../styles/certificate-verify.css';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:5000/api';
const QRCODE_DECODER_CDN = 'https://cdn.jsdelivr.net/npm/jsqr@1.4.0/dist/jsQR.js';

export default function CertificateVerify() {
  const location = useLocation();
  const params = useParams();
  const [activeTab, setActiveTab] = useState('id');
  const [certificateId, setCertificateId] = useState('');
  const [searchEmail, setSearchEmail] = useState('');
  const [result, setResult] = useState(null);
  const [loading, setLoading] = useState(false);
  const [qrImage, setQrImage] = useState(null);
  const [showQRScanner, setShowQRScanner] = useState(false);
  const [imageError, setImageError] = useState(false);
  const routeParamCertificateId = params.certificateId;
  const routeWildcardCertificateId = params['*'];

  const verifyCertificateById = useCallback(async (e, idToVerify) => {
    if (e?.preventDefault) {
      e.preventDefault();
    }

    const verificationId = `${idToVerify || ''}`.trim();

    if (!verificationId) {
      setResult({ type: 'error', message: 'Please enter a certificate ID' });
      return;
    }

    setCertificateId(verificationId);
    setLoading(true);

    try {
      // Encode the certificate ID because IDs such as DOF/CP/1226-01
      // contain "/" characters.
      const encodedId = encodeURIComponent(verificationId);

      const response = await axios.get(
        `${API_URL}/certificates/verify?certificateId=${encodedId}`,
        { timeout: 10000 }
      );

      if (response.data.success) {
        setResult({
          type: 'success',
          message: 'Certificate is valid and authentic!',
          data: response.data.certificate
        });
      }
    } catch (error) {
      if (error.response?.status === 404) {
        setResult({
          type: 'error',
          message: 'Certificate not found. Please check the ID.'
        });
      } else if (error.response?.status === 400) {
        setResult({
          type: 'expired',
          message: error.response.data.message,
          data: error.response.data.certificate
        });
      } else {
        setResult({
          type: 'error',
          message: 'Error verifying certificate. Please try again.'
        });
      }
    } finally {
      setLoading(false);
    }
  }, []);

  const handleVerifyById = (e, idToVerify = certificateId) => {
    return verifyCertificateById(e, idToVerify);
  };

  const handleSearchByEmail = async (e) => {
    e.preventDefault();

    if (!searchEmail.trim()) {
      setResult({
        type: 'error',
        message: 'Please enter an email address'
      });
      return;
    }

    setLoading(true);

    try {
      const response = await axios.get(
        `${API_URL}/certificates/recipient/${searchEmail}`,
        { timeout: 10000 }
      );

      if (response.data.success) {
        setResult({
          type: 'success',
          message: `Found ${response.data.count} certificate(s) for this email`,
          data: response.data.certificates,
          isMultiple: true
        });
      } else {
        setResult({
          type: 'error',
          message: 'No certificates found for this email'
        });
      }
    } catch (error) {
      setResult({
        type: 'error',
        message: 'Error searching certificates. Please try again.'
      });
    } finally {
      setLoading(false);
    }
  };

  const extractCertificateIdFromQrPayload = (value) => {
    if (!value) return null;

    const trimmed = `${value}`.trim();

    if (!trimmed) return null;

    const jsonMatch = trimmed.match(/"certificateId"\s*:\s*"([^"]+)"/);

    if (jsonMatch?.[1]) {
      return jsonMatch[1];
    }

    const prefixMatch = trimmed.match(/dofan-cert::([A-Za-z0-9 /._-]+)/i);

    if (prefixMatch?.[1]) {
      return prefixMatch[1];
    }

    // URLSearchParams encodes spaces as "+", which decodeURIComponent leaves alone.
    const queryMatch = trimmed.match(/[?&]certificateId=([^&#]+)/i);

    if (queryMatch?.[1]) {
      return decodeURIComponent(queryMatch[1].replace(/\+/g, ' '));
    }

    // Support certificate IDs containing "/" when reading the QR URL.
    const urlMatch = trimmed.match(/\/verify\/([^?/#]+)/i);

    if (urlMatch?.[1]) {
      return decodeURIComponent(urlMatch[1]);
    }

    return trimmed;
  };

  const loadQrDecoder = () => new Promise((resolve, reject) => {
    if (window.jsQR) return resolve();

    const existingScript = document.querySelector(
      `script[src="${QRCODE_DECODER_CDN}"]`
    );

    if (existingScript) {
      existingScript.addEventListener('load', resolve, { once: true });
      existingScript.addEventListener(
        'error',
        () => reject(new Error('QR decoder failed to load')),
        { once: true }
      );
      return;
    }

    const script = document.createElement('script');

    script.src = QRCODE_DECODER_CDN;

    script.onload = () => resolve();

    script.onerror = () =>
      reject(new Error('QR decoder failed to load'));

    document.body.appendChild(script);
  });

  const decodeQrFromImage = (imageDataUrl) => new Promise((resolve, reject) => {
    const image = new Image();

    image.onload = () => {
      const canvas = document.createElement('canvas');

      const context = canvas.getContext('2d', {
        willReadFrequently: true
      });

      canvas.width = image.width;
      canvas.height = image.height;

      context.drawImage(
        image,
        0,
        0,
        canvas.width,
        canvas.height
      );

      const imageData = context.getImageData(
        0,
        0,
        canvas.width,
        canvas.height
      );

      const code = window.jsQR(
        imageData.data,
        canvas.width,
        canvas.height
      );

      if (code?.data) {
        resolve(code.data);
      } else {
        reject(new Error('No QR code detected in the image'));
      }
    };

    image.onerror = () =>
      reject(new Error('Unable to read the selected image'));

    image.src = imageDataUrl;
  });

  const handleQRUpload = async (e) => {
    const file = e.target.files[0];

    if (!file) return;

    const reader = new FileReader();

    reader.onload = async (event) => {
      const imageDataUrl = event.target.result;

      setQrImage(imageDataUrl);

      try {
        await loadQrDecoder();

        const decodedValue = await decodeQrFromImage(imageDataUrl);

        const certificateIdFromQr =
          extractCertificateIdFromQrPayload(decodedValue);

        if (!certificateIdFromQr) {
          setResult({
            type: 'error',
            message:
              'The QR image could not be read. Please try a clearer photo.'
          });

          return;
        }

        handleQRScan(certificateIdFromQr);
      } catch (error) {
        setResult({
          type: 'error',
          message:
            error.message ||
            'QR Code image decoding requires a clearer image or camera scanning.'
        });
      }
    };

    reader.readAsDataURL(file);
  };

  const handleQRScan = (qrValue) => {
    const certificateIdFromQr =
      extractCertificateIdFromQrPayload(qrValue);

    setCertificateId(
      certificateIdFromQr || qrValue || ''
    );

    setShowQRScanner(false);
    setActiveTab('id');

    setTimeout(() => {
      handleVerifyById(
        null,
        certificateIdFromQr || qrValue || ''
      );
    }, 100);
  };

  useEffect(() => {
    const routeCertificateId =
      new URLSearchParams(location.search).get('certificateId') ||
      routeParamCertificateId ||
      routeWildcardCertificateId;

    if (routeCertificateId) {
      setCertificateId(routeCertificateId);
      setActiveTab('id');

      setTimeout(() => {
        verifyCertificateById(null, routeCertificateId);
      }, 100);
    }
  }, [
    location.search,
    routeParamCertificateId,
    routeWildcardCertificateId,
    verifyCertificateById
  ]);

  const getRenderResult = () => {
    if (!result) return null;

    if (result.isMultiple && Array.isArray(result.data)) {
      return (
        <div className={`result-container ${result.type}`}>
          <div className="result-header">
            <span className="result-icon">
              {result.type === 'success' && <FaCheckCircle />}
              {result.type === 'error' && <FaTimesCircle />}
            </span>

            <span className="result-message">
              {result.message}
            </span>
          </div>

          <div className="multiple-list">
            {result.data.map((cert, idx) => (
              <div
                key={idx}
                className="multiple-cert-card"
              >
                {cert.certificateImage && (
                  <div className="cert-card-image">
                    <img
                      src={cert.certificateImage}
                      alt={cert.certificateTitle}
                      className="cert-card-img"
                    />
                  </div>
                )}

                <strong>{cert.certificateTitle}</strong>

                <p className="muted">
                  ID: {cert.certificateId}
                </p>

                <p className="muted">
                  Issued:{' '}
                  {new Date(cert.issuedDate).toLocaleDateString()}
                </p>
              </div>
            ))}
          </div>
        </div>
      );
    }

    if (result.type === 'success' && result.data) {
      const cert = result.data;

      return (
        <div className="result-container success">
          <div className="result-header">
            <span className="result-icon">
              <FaCheckCircle />
            </span>

            <span className="result-message">
              {result.message}
            </span>
          </div>

          {cert.certificateImage && !imageError && (
            <div className="certificate-image-section">
              <h3>Certificate</h3>

              <div className="certificate-image-wrapper">
                <img
                  src={cert.certificateImage}
                  alt="Certificate"
                  className="certificate-image"
                  onError={() => setImageError(true)}
                  onLoad={() => setImageError(false)}
                />

                {cert.qrCode && (
                  <div className="certificate-qr-overlay">
                    <img
                      src={cert.qrCode}
                      alt="Certificate QR code"
                      className="certificate-qr-image"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          <div className="certificate-details">
            <div className="detail-row">
              <span className="detail-label">
                Certificate ID:
              </span>

              <span className="detail-value">
                {cert.certificateId}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">
                Recipient:
              </span>

              <span className="detail-value">
                {cert.recipientName}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">
                Title:
              </span>

              <span className="detail-value">
                {cert.certificateTitle}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">
                Type:
              </span>

              <span className="detail-value">
                {cert.certificateType}
              </span>
            </div>

            <div className="detail-row">
              <span className="detail-label">
                Issued Date:
              </span>

              <span className="detail-value">
                {new Date(cert.issuedDate).toLocaleDateString()}
              </span>
            </div>

            {cert.expiryDate && (
              <div className="detail-row">
                <span className="detail-label">
                  Expiry Date:
                </span>

                <span className="detail-value">
                  {new Date(cert.expiryDate).toLocaleDateString()}
                </span>
              </div>
            )}

            {cert.grade && (
              <div className="detail-row">
                <span className="detail-label">
                  Grade:
                </span>

                <span className="detail-value">
                  {cert.grade}
                </span>
              </div>
            )}

            {cert.skills && cert.skills.length > 0 && (
              <div className="detail-row">
                <span className="detail-label">
                  Skills:
                </span>

                <span className="detail-value">
                  {cert.skills.join(', ')}
                </span>
              </div>
            )}

            {cert.issuerCompanyId && (
              <div style={{ marginTop: '1rem' }}>
                {cert.issuerCompanyId._id ? (
                  <Link
                    to={`/company/${cert.issuerCompanyId._id}`}
                    className="company-badge"
                  >
                    Issued by:{' '}
                    {cert.issuerCompanyId.companyName ||
                      cert.issuerName}
                  </Link>
                ) : (
                  <span className="company-badge">
                    Issued by:{' '}
                    {cert.issuerCompanyId.companyName ||
                      cert.issuerName}
                  </span>
                )}
              </div>
            )}
          </div>
        </div>
      );
    }

    if (result.type === 'expired') {
      const cert = result.data;

      return (
        <div className="result-container expired">
          <div className="result-header">
            <span className="result-icon">
              <FaExclamationTriangle />
            </span>

            <span className="result-message">
              {result.message}
            </span>
          </div>

          {cert.certificateImage && !imageError && (
            <div className="certificate-image-section">
              <h3>Certificate</h3>

              <div className="certificate-image-wrapper">
                <img
                  src={cert.certificateImage}
                  alt="Certificate"
                  className="certificate-image"
                  onError={() => setImageError(true)}
                  onLoad={() => setImageError(false)}
                />

                {cert.qrCode && (
                  <div className="certificate-qr-overlay">
                    <img
                      src={cert.qrCode}
                      alt="Certificate QR code"
                      className="certificate-qr-image"
                    />
                  </div>
                )}
              </div>
            </div>
          )}

          {cert && (
            <div className="certificate-details">
              <div className="detail-row">
                <span className="detail-label">
                  Certificate ID:
                </span>

                <span className="detail-value">
                  {cert.certificateId}
                </span>
              </div>

              <div className="detail-row">
                <span className="detail-label">
                  Expired Date:
                </span>

                <span className="detail-value">
                  {new Date(cert.expiryDate).toLocaleDateString()}
                </span>
              </div>
            </div>
          )}
        </div>
      );
    }

    return (
      <div className={`result-container ${result.type}`}>
        <div className="result-header">
          <span className="result-icon">
            {result.type === 'error' && <FaTimesCircle />}
          </span>

          <span className="result-message">
            {result.message}
          </span>
        </div>
      </div>
    );
  };

  const isSpecificCertificateUrl = Boolean(
    routeParamCertificateId ||
    routeWildcardCertificateId ||
    new URLSearchParams(location.search).get('certificateId')
  );

  return (
    <div className="certificate-verify-container">
      {/* A URL pointing at one certificate shows a named person's record, so it is kept out of search results. */}
      <SEO
        title="Verify a Certificate"
        description="Verify the authenticity and validity of a Dofcan Oilserve training certificate by certificate ID, recipient email or QR code."
        path="/verify"
        noindex={isSpecificCertificateUrl}
      />

      {showQRScanner && (
        <QRScanner
          onScan={handleQRScan}
          onClose={() => setShowQRScanner(false)}
        />
      )}

      <div className="certificate-verify-wrapper">
        <div className="verify-header">
          <h1>Verify Certificate</h1>
          <p>Authenticate and validate certificates instantly</p>
        </div>

        <div className="verify-tabs">
          <button
            className={`tab-button ${
              activeTab === 'id' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('id')}
          >
            Certificate ID
          </button>

          <button
            className={`tab-button ${
              activeTab === 'email' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('email')}
          >
            Search by Email
          </button>

          <button
            className={`tab-button ${
              activeTab === 'qr' ? 'active' : ''
            }`}
            onClick={() => setActiveTab('qr')}
          >
            QR Code
          </button>
        </div>

        {activeTab === 'id' && (
          <form onSubmit={handleVerifyById}>
            <div className="form-group">
              <label htmlFor="cert-id">
                Certificate ID
              </label>

              <input
                id="cert-id"
                type="text"
                placeholder="Enter certificate ID (e.g., CERT-2026-001)"
                value={certificateId}
                onChange={(e) =>
                  setCertificateId(e.target.value)
                }
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="verify-button"
              disabled={loading}
            >
              {loading
                ? 'Verifying...'
                : 'Verify Certificate'}
            </button>
          </form>
        )}

        {activeTab === 'email' && (
          <form onSubmit={handleSearchByEmail}>
            <div className="form-group">
              <label htmlFor="search-email">
                Email Address
              </label>

              <input
                id="search-email"
                type="email"
                placeholder="Enter recipient email"
                value={searchEmail}
                onChange={(e) =>
                  setSearchEmail(e.target.value)
                }
                disabled={loading}
              />
            </div>

            <button
              type="submit"
              className="verify-button"
              disabled={loading}
            >
              {loading
                ? 'Searching...'
                : 'Search Certificates'}
            </button>
          </form>
        )}

        {activeTab === 'qr' && (
          <div className="qr-import-section">
            <p className="qr-note">
              Use live camera or upload a QR code image
            </p>

            <button
              type="button"
              onClick={() => setShowQRScanner(true)}
              className="scan-button"
            >
              <FaCamera /> Scan with Camera
            </button>

            <p className="qr-or">OR</p>

            <label className="qr-import-label">
              <input
                type="file"
                className="qr-file-input"
                accept="image/*"
                onChange={handleQRUpload}
              />

              Choose QR Code Image
            </label>

            {qrImage && (
              <div className="qr-preview">
                <img
                  src={qrImage}
                  alt="QR Code"
                  className="qr-image"
                />
              </div>
            )}
          </div>
        )}

        {loading && (
          <div className="loading-spinner">
            <SimpleSpinner />
          </div>
        )}

        {getRenderResult()}
      </div>
    </div>
  );
}
